import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LoadingSpinner } from '../components/UIComponents';
import { rentalCalcAPI } from '../utils/api';
import {
  Calculator, Plus, Send, CheckCircle, Lock, AlertTriangle,
  ChevronDown, Loader2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_INPUTS = {
  e_kwh: '', pcs_kw: '', capex: '', gst: '18',
};
const DEFAULT_LEVERS = {
  irr: '18', tenor_years: '5', deposit_months: '3',
  min_commit_pct: '70', eta: '95',
};

const CALC_STATUS = {
  DRAFT: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  SUBMITTED: { label: 'In Review', color: 'bg-amber-50 text-amber-700' },
  APPROVED: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700' },
  LOCKED: { label: 'Locked', color: 'bg-indigo-50 text-indigo-700' },
};

const CalcStatusBadge = ({ status }) => {
  const cfg = CALC_STATUS[status] || CALC_STATUS.DRAFT;
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>;
};

const KV = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono font-medium ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</span>
  </div>
);

const FI = ({ label, value, onChange, tid, prefix, suffix, hint }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="relative mt-1">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{prefix}</span>}
      <Input type="number" step="any" value={value} onChange={e => onChange(e.target.value)}
        className={`${prefix ? 'pl-7' : ''} ${suffix ? 'pr-14' : ''}`} data-testid={tid} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{suffix}</span>}
    </div>
    {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

// ==================== RENTAL CALC DETAIL ====================
export function RentalCalcDetail({ calcId, onRefresh }) {
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await rentalCalcAPI.get(calcId); setCalc(r.data); }
      catch { toast.error('Failed to load calculation'); }
      finally { setLoading(false); }
    };
    load();
  }, [calcId]);

  if (loading) return <LoadingSpinner />;
  if (!calc) return null;

  const { inputs, levers, outputs, status } = calc;
  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
  const pct = (n) => n != null ? `${(Number(n) * 100).toFixed(2)}%` : '—';

  const handleSubmit = async () => {
    try { await rentalCalcAPI.submit(calcId); toast.success('Submitted for approval'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };
  const handleApprove = async () => {
    try { await rentalCalcAPI.approve(calcId, 'approve', ''); toast.success('Approved'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };
  const handleReject = async () => {
    try { await rentalCalcAPI.approve(calcId, 'reject', ''); toast.success('Rejected — returned to draft'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleLock = async () => {
    try { await rentalCalcAPI.lock(calcId); toast.success('Calculation locked'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to lock'); }
  };

  return (
    <div className="space-y-4" data-testid={`calc-detail-${calcId}`}>
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calculator className="w-5 h-5 text-slate-400" />
          <div>
            <p className="font-semibold text-slate-900">{calc.notes || 'Rental Calculation'}</p>
            <p className="text-xs text-slate-500">Created {new Date(calc.created_at).toLocaleDateString()}</p>
          </div>
          <CalcStatusBadge status={status} />
        </div>
        <div className="flex items-center space-x-2">
          {status === 'DRAFT' && <Button size="sm" onClick={handleSubmit} className="bg-slate-900" data-testid="submit-calc-btn"><Send className="w-3.5 h-3.5 mr-1.5" /> Submit for Review</Button>}
          {status === 'SUBMITTED' && (
            <>
              <Button size="sm" variant="outline" onClick={handleReject} data-testid="reject-calc-btn">Reject</Button>
              <Button size="sm" onClick={handleApprove} className="bg-emerald-600" data-testid="approve-calc-btn"><CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve</Button>
            </>
          )}
          {status === 'APPROVED' && <Button size="sm" onClick={handleLock} className="bg-indigo-600" data-testid="lock-calc-btn"><Lock className="w-3.5 h-3.5 mr-1.5" /> Lock Calculation</Button>}
          {status === 'LOCKED' && <span className="text-xs text-indigo-600 font-semibold flex items-center"><Lock className="w-3.5 h-3.5 mr-1" /> Immutable</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* INPUTS */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <p className="section-label">Inputs (Maker)</p>
          </div>
          <div className="p-4 space-y-2 text-xs">
            <KV label="Battery Energy (E)" value={`${inputs.e_kwh?.toLocaleString() || 0} kWh`} />
            <KV label="PCS Rated Power" value={`${inputs.pcs_kw?.toLocaleString() || 0} kW`} />
            <KV label="Capex (ex-GST)" value={fmt(inputs.capex)} />
            <KV label="GST Rate" value={pct(inputs.gst)} />
          </div>
        </div>

        {/* LEVERS */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
            <p className="text-xs font-semibold text-amber-700 uppercase font-heading tracking-wide">Approver Levers</p>
          </div>
          <div className="p-4 space-y-2 text-xs">
            <KV label="Target IRR" value={pct(levers.irr)} />
            <KV label="Tenor" value={`${levers.tenor_years} years`} />
            <KV label="Deposit Months" value={`${levers.deposit_months} months`} />
            <KV label="Min Commit" value={pct(levers.min_commit_pct)} />
            <KV label="Efficiency (eta)" value={pct(levers.eta)} />
          </div>
        </div>

        {/* OUTPUTS */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200">
            <p className="text-xs font-semibold text-emerald-700 uppercase font-heading tracking-wide">Outputs</p>
          </div>
          <div className="p-4 space-y-2">
            <div className="bg-slate-900 rounded-sm p-3 text-center mb-3">
              <p className="text-[10px] text-slate-400 uppercase font-heading tracking-wide">Service Fee (ex-GST)</p>
              <p className="text-2xl font-mono font-medium text-white" data-testid="output-price-kwh">₹{outputs.service_fee_ex_gst?.toFixed(2)}</p>
              <p className="text-[10px] font-mono text-slate-400">incl GST: ₹{outputs.service_fee_incl_gst?.toFixed(2)}</p>
            </div>
            <div className="space-y-2 text-xs">
              <KV label="CRF" value={outputs.crf?.toFixed(6) || '—'} />
              <KV label="Annual Recovery" value={fmt(outputs.annual_recovery)} />
              <KV label="Annual Units" value={`${outputs.annual_units?.toLocaleString('en-IN') || 0} kWh`} />
              <div className="border-t border-slate-100 pt-2 mt-2" />
              <KV label="Base Units Monthly" value={`${outputs.base_units_monthly?.toLocaleString('en-IN') || 0} kWh/mo`} />
              <KV label="Min Billable (70%)" value={`${outputs.min_billable_monthly?.toLocaleString('en-IN') || 0} kWh/mo`} />
              <KV label="Hours @ Rated Power" value={`${outputs.hours_at_rated_power || 0} h`} />
              <div className="border-t border-slate-100 pt-2 mt-2" />
              <KV label="Min Monthly Invoice (incl GST)" value={fmt(outputs.min_monthly_invoice_incl_gst)} highlight />
              <KV label="Deposit" value={fmt(outputs.deposit)} />
            </div>
            {outputs.data_quality_flags?.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                <AlertTriangle className="w-3 h-3 inline mr-1" />{outputs.data_quality_flags.join('; ')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Part I: Line Items / Bundle */}
      {calc.line_items?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <p className="section-label">Solution Bundle ({calc.line_items.length} items)</p>
            {calc.bundle_summary && (
              <span className="text-xs font-mono text-slate-500">
                {calc.bundle_summary.total_kw} kW / {calc.bundle_summary.total_kwh} kWh — {calc.bundle_summary.unit_count} units
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="calc-line-items-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2 px-4 font-medium text-slate-500">Vendor / Model</th>
                  <th className="text-right py-2 px-4 font-medium text-slate-500">kW</th>
                  <th className="text-right py-2 px-4 font-medium text-slate-500">kWh</th>
                  <th className="text-right py-2 px-4 font-medium text-slate-500">Qty</th>
                  <th className="text-right py-2 px-4 font-medium text-slate-500">Unit Cost</th>
                  <th className="text-right py-2 px-4 font-medium text-slate-500">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {calc.line_items.map((li, i) => (
                  <tr key={li.line_item_id || i}>
                    <td className="py-1.5 px-4"><span className="font-medium">{li.vendor_name}</span> <span className="text-slate-400">{li.pcs_model} × {li.battery_model}</span></td>
                    <td className="py-1.5 px-4 text-right font-mono">{li.pcs_rated_power_kw}</td>
                    <td className="py-1.5 px-4 text-right font-mono">{li.battery_rated_energy_kwh}</td>
                    <td className="py-1.5 px-4 text-right font-mono font-medium">{li.quantity}</td>
                    <td className="py-1.5 px-4 text-right font-mono">{fmt(li.unit_cost)}</td>
                    <td className="py-1.5 px-4 text-right font-mono font-medium">{fmt(li.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={5} className="py-2 px-4 text-right font-semibold text-slate-600">Total Capex</td>
                  <td className="py-2 px-4 text-right font-mono font-medium text-slate-900">{fmt(calc.bundle_summary?.total_capex)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

// ==================== NEW CALC DIALOG ====================
export function NewCalcDialog({ siteId, configSnapshot, onClose, onCreated }) {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [levers, setLevers] = useState(DEFAULT_LEVERS);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoPopData, setAutoPopData] = useState(null);
  const [loadingAutoPop, setLoadingAutoPop] = useState(true);

  // Part I: Solution bundle state
  const [lineItems, setLineItems] = useState([]);
  const [matrixEntries, setMatrixEntries] = useState([]);
  const [showBundleBuilder, setShowBundleBuilder] = useState(false);

  useEffect(() => {
    // If we have a locked configuration snapshot, use it directly
    if (configSnapshot) {
      const opt = configSnapshot.selected_option || {};
      const pricing = configSnapshot.pricing_match;

      const data = {
        config_found: true,
        config_option: {
          option_name: opt.option_name || '',
          pcs_power_kw: opt.inverter_power_kw || 0,
          battery_energy_kwh: opt.battery_energy_kwh || 0,
          backup_hours: opt.recommended_backup_hours || 0,
        },
        pricing_found: !!pricing,
        pricing_entry: pricing,
      };
      setAutoPopData(data);

      setInputs(prev => ({
        ...prev,
        e_kwh: opt.battery_energy_kwh || '',
        pcs_kw: opt.inverter_power_kw || '',
        capex: pricing?.total_bess_cost_INR || '',
      }));
      setNotes(`${opt.option_name || 'Config'} — ${opt.inverter_power_kw || 0}kW/${opt.battery_energy_kwh || 0}kWh`);
      setLoadingAutoPop(false);
      return;
    }

    // Fallback: try the API auto-populate
    const loadAutoPopulate = async () => {
      try {
        const res = await rentalCalcAPI.autoPopulate(siteId);
        const data = res.data;
        setAutoPopData(data);
        if (data.config_found && data.suggested_inputs) {
          const si = data.suggested_inputs;
          setInputs(prev => ({
            ...prev,
            e_kwh: si.e_kwh || '',
            pcs_kw: si.pcs_kw || '',
            capex: si.capex || '',
            gst: si.gst ? String(si.gst * 100) : '18',
          }));
          const cfg = data.config_option;
          if (cfg) setNotes(`${cfg.option_name} — ${cfg.pcs_power_kw}kW/${cfg.battery_energy_kwh}kWh`);
        }
        // Apply suggested levers from rental defaults
        if (data.suggested_levers) {
          const sl = data.suggested_levers;
          setLevers(prev => ({
            ...prev,
            irr: sl.irr ? String(sl.irr * 100) : prev.irr,
            tenor_years: sl.tenor_years ? String(sl.tenor_years) : prev.tenor_years,
            deposit_months: sl.deposit_months ? String(sl.deposit_months) : prev.deposit_months,
            min_commit_pct: sl.min_commit_pct ? String(sl.min_commit_pct * 100) : prev.min_commit_pct,
            eta: sl.eta ? String(sl.eta * 100) : prev.eta,
          }));
        }
      } catch (e) { console.error('RentalCalculator autopopulate failed', e); }
      finally { setLoadingAutoPop(false); }
    };
    loadAutoPopulate();
  }, [siteId, configSnapshot]);

  // Fetch pricing matrix for bundle builder
  useEffect(() => {
    const fetchMatrix = async () => {
      try { const res = await rentalCalcAPI.pricingMatrixSearch(); setMatrixEntries(res.data || []); }
      catch (e) { console.error('pricingMatrixSearch failed', e); }
    };
    fetchMatrix();
  }, []);

  // Recalculate capex when line items change
  useEffect(() => {
    if (lineItems.length > 0) {
      const total = lineItems.reduce((sum, item) => sum + (item.unit_cost * item.quantity), 0);
      const totalKwh = lineItems.reduce((s, i) => s + i.battery_rated_energy_kwh * i.quantity, 0);
      const totalKw = lineItems.reduce((s, i) => s + i.pcs_rated_power_kw * i.quantity, 0);
      setInputs(prev => ({
        ...prev,
        capex: total || '',
        e_kwh: totalKwh || prev.e_kwh,
        pcs_kw: totalKw || prev.pcs_kw,
      }));
    }
  }, [lineItems]);

  const addLineItem = (entry) => {
    setLineItems(prev => [...prev, {
      matrix_id: entry.matrix_entry_id || '',
      vendor_name: entry.vendor_name || '',
      pcs_model: entry.pcs_model || '',
      battery_model: entry.battery_model || '',
      pcs_rated_power_kw: entry.pcs_rated_power_kw || 0,
      battery_rated_energy_kwh: entry.battery_rated_energy_kwh || 0,
      quantity: 1,
      unit_cost: entry.total_bess_cost_INR || 0,
    }]);
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeLineItem = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const bundleSummary = lineItems.length > 0 ? {
    totalKw: lineItems.reduce((s, i) => s + i.pcs_rated_power_kw * i.quantity, 0),
    totalKwh: lineItems.reduce((s, i) => s + i.battery_rated_energy_kwh * i.quantity, 0),
    totalCapex: lineItems.reduce((s, i) => s + i.unit_cost * i.quantity, 0),
    unitCount: lineItems.reduce((s, i) => s + i.quantity, 0),
  } : null;

  const num = (v) => v ? Number(v) : 0;
  const pctToDec = (v) => v ? Number(v) / 100 : 0;
  const fmtInr = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '';

  const buildInputsPayload = () => ({
    e_kwh: num(inputs.e_kwh),
    pcs_kw: num(inputs.pcs_kw),
    capex: num(inputs.capex),
    gst: pctToDec(inputs.gst),
  });
  const buildLeversPayload = () => ({
    irr: pctToDec(levers.irr),
    tenor_years: num(levers.tenor_years),
    deposit_months: num(levers.deposit_months),
    min_commit_pct: pctToDec(levers.min_commit_pct),
    eta: pctToDec(levers.eta),
  });

  const handlePreview = async () => {
    try {
      const res = await rentalCalcAPI.compute(buildInputsPayload(), buildLeversPayload());
      setPreview(res.data);
    } catch { toast.error('Compute failed'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await rentalCalcAPI.create({
        prospect_site_id: siteId,
        inputs: buildInputsPayload(),
        levers: buildLeversPayload(),
        notes,
        line_items: lineItems.map(li => ({
          matrix_id: li.matrix_id,
          vendor_name: li.vendor_name,
          pcs_model: li.pcs_model,
          battery_model: li.battery_model,
          pcs_rated_power_kw: li.pcs_rated_power_kw,
          battery_rated_energy_kwh: li.battery_rated_energy_kwh,
          quantity: Number(li.quantity),
          unit_cost: Number(li.unit_cost),
        })),
      });
      toast.success('Calculation saved');
      onCreated(res.data.rental_calc_id);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const setI = (k, v) => setInputs(p => ({...p, [k]: v}));
  const setL = (k, v) => setLevers(p => ({...p, [k]: v}));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Rental Calculation</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {loadingAutoPop ? (
            <div className="flex items-center text-xs text-slate-400 py-2"><Loader2 className="w-3 h-3 mr-2 animate-spin" />Loading configurator & pricing data...</div>
          ) : autoPopData?.config_found ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3" data-testid="auto-populate-summary">
              <p className="text-xs font-semibold text-indigo-700 mb-1">Auto-populated from Configurator + Pricing Matrix</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-indigo-600">
                {autoPopData.config_option && (
                  <>
                    <span>Config: <b>{autoPopData.config_option.option_name}</b></span>
                    <span>PCS: <b>{autoPopData.config_option.pcs_power_kw} kW</b></span>
                    <span>Battery: <b>{autoPopData.config_option.battery_energy_kwh} kWh</b></span>
                  </>
                )}
                {autoPopData.pricing_found && autoPopData.pricing_entry && (
                  <>
                    <span>SKU: <b>{autoPopData.pricing_entry.pcs_sku} × {autoPopData.pricing_entry.battery_sku}</b></span>
                    {autoPopData.pricing_entry.total_bess_cost_INR && (
                      <span>BESS Cost: <b>{fmtInr(autoPopData.pricing_entry.total_bess_cost_INR)}</b></span>
                    )}
                  </>
                )}
                {!autoPopData.pricing_found && (
                  <span className="text-amber-600">No matching pricing — enter asset cost manually</span>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <p className="text-xs text-slate-500">No configurator data — enter values manually</p>
            </div>
          )}

          <div><Label>Label / Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Option 1 — 500kW/723kWh" className="mt-1" data-testid="calc-notes-input" /></div>

          {/* Part I: Solution Bundle Builder */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-xs font-semibold text-slate-600 uppercase hover:bg-slate-100 transition-colors"
              onClick={() => setShowBundleBuilder(!showBundleBuilder)}
              data-testid="toggle-bundle-builder"
            >
              <span>Solution Bundle {lineItems.length > 0 ? `(${lineItems.length} items)` : '— build from vendor SKUs'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showBundleBuilder ? 'rotate-180' : ''}`} />
            </button>
            {showBundleBuilder && (
              <div className="p-4 space-y-3">
                {/* Line Items Table */}
                {lineItems.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="bundle-line-items">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-1 px-2 font-medium text-slate-500">Vendor / Model</th>
                          <th className="text-right py-1 px-2 font-medium text-slate-500">kW</th>
                          <th className="text-right py-1 px-2 font-medium text-slate-500">kWh</th>
                          <th className="text-right py-1 px-2 font-medium text-slate-500 w-16">Qty</th>
                          <th className="text-right py-1 px-2 font-medium text-slate-500">Unit Cost</th>
                          <th className="text-right py-1 px-2 font-medium text-slate-500">Total</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, idx) => (
                          <tr key={idx} className="border-b border-slate-50" data-testid={`line-item-${idx}`}>
                            <td className="py-1.5 px-2">
                              <span className="font-medium">{li.vendor_name}</span>
                              <span className="text-slate-400 ml-1">{li.pcs_model} × {li.battery_model}</span>
                            </td>
                            <td className="py-1.5 px-2 text-right">{li.pcs_rated_power_kw}</td>
                            <td className="py-1.5 px-2 text-right">{li.battery_rated_energy_kwh}</td>
                            <td className="py-1.5 px-2"><Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', Number(e.target.value) || 1)} className="h-6 text-xs w-14 text-right" /></td>
                            <td className="py-1.5 px-2"><Input type="number" value={li.unit_cost} onChange={e => updateLineItem(idx, 'unit_cost', Number(e.target.value) || 0)} className="h-6 text-xs w-24 text-right" /></td>
                            <td className="py-1.5 px-2 text-right font-medium">₹{(li.unit_cost * li.quantity).toLocaleString('en-IN')}</td>
                            <td className="py-1.5 px-2 text-center"><button onClick={() => removeLineItem(idx)} className="text-slate-300 hover:text-red-500">×</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* Bundle Summary */}
                    {bundleSummary && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 text-xs" data-testid="bundle-summary">
                        <div className="flex space-x-4 text-slate-500">
                          <span>Total: <b>{bundleSummary.totalKw} kW</b> / <b>{bundleSummary.totalKwh} kWh</b></span>
                          <span><b>{bundleSummary.unitCount}</b> units</span>
                        </div>
                        <span className="font-bold text-slate-900">₹{bundleSummary.totalCapex.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Add from pricing matrix */}
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Add Vendor SKU</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto border border-slate-100 rounded p-2">
                    {matrixEntries.length === 0 ? (
                      <p className="text-xs text-slate-400 col-span-2 text-center py-2">No pricing matrix entries found</p>
                    ) : matrixEntries.map(entry => (
                      <button
                        key={entry.matrix_entry_id}
                        className="text-left px-2 py-1.5 rounded text-xs hover:bg-indigo-50 transition-colors border border-slate-50 hover:border-indigo-200 flex items-center justify-between"
                        onClick={() => addLineItem(entry)}
                        data-testid={`add-sku-${entry.matrix_entry_id}`}
                      >
                        <div>
                          <span className="font-medium">{entry.vendor_name}</span>
                          <span className="text-slate-400 ml-1">{entry.pcs_rated_power_kw}kW / {entry.battery_rated_energy_kwh}kWh</span>
                        </div>
                        <span className="text-emerald-600 font-medium ml-2">{entry.total_bess_cost_INR ? `₹${(entry.total_bess_cost_INR / 100000).toFixed(1)}L` : '—'}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-semibold text-slate-600 uppercase mb-3">System Inputs</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FI label="Battery Energy (E_kWh)" value={inputs.e_kwh} onChange={v => setI('e_kwh', v)} tid="input-e-kwh" suffix="kWh" hint="Total energy capacity" />
              <FI label="PCS Power (PCS_kW)" value={inputs.pcs_kw} onChange={v => setI('pcs_kw', v)} tid="input-pcs-kw" suffix="kW" hint="Rated power" />
              <FI label="Capex (ex-GST)" value={inputs.capex} onChange={v => setI('capex', v)} tid="input-capex" prefix="₹" hint="Total asset cost" />
              <FI label="GST Rate" value={inputs.gst} onChange={v => setI('gst', v)} suffix="%" />
            </div>
          </div>

          <div className="border border-amber-200 rounded-sm p-4 bg-amber-50/30">
            <p className="text-xs font-semibold text-amber-700 uppercase mb-3">Financial Levers (from Rental Defaults)</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <FI label="Target IRR" value={levers.irr} onChange={v => setL('irr', v)} tid="lever-irr" suffix="%" />
              <FI label="Tenor" value={levers.tenor_years} onChange={v => setL('tenor_years', v)} tid="lever-tenor" suffix="years" />
              <FI label="Deposit" value={levers.deposit_months} onChange={v => setL('deposit_months', v)} suffix="months" />
              <FI label="Min Commit" value={levers.min_commit_pct} onChange={v => setL('min_commit_pct', v)} suffix="%" />
              <FI label="Efficiency" value={levers.eta} onChange={v => setL('eta', v)} suffix="%" />
            </div>
          </div>

          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">CRF Preview</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><span className="text-slate-500">Fee/kWh:</span> <span className="font-bold text-slate-900">₹{preview.service_fee_ex_gst?.toFixed(2)}</span></div>
                <div><span className="text-slate-500">Min Monthly:</span> <span className="font-bold">₹{preview.min_monthly_invoice_incl_gst?.toLocaleString('en-IN')}</span></div>
                <div><span className="text-slate-500">Deposit:</span> <span className="font-bold">₹{preview.deposit?.toLocaleString('en-IN')}</span></div>
                <div><span className="text-slate-500">Hours @ rated:</span> <span className="font-bold">{preview.hours_at_rated_power} h</span></div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="outline" onClick={handlePreview} data-testid="preview-calc-btn">Preview</Button>
            <Button className="bg-slate-900" disabled={saving} onClick={handleSave} data-testid="save-calc-btn">{saving ? 'Saving...' : 'Save Draft'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== RENTAL CALC LIST (Inline in Proposal Tab) ====================
export function RentalCalcSection({ siteId, configSnapshot }) {
  const [calcs, setCalcs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [activeCalcId, setActiveCalcId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCalcs = useCallback(async () => {
    try {
      const res = await rentalCalcAPI.list({ prospect_site_id: siteId });
      setCalcs(res.data || []);
      if (res.data?.length > 0) setActiveCalcId(prev => prev || res.data[0].rental_calc_id);
    } catch (e) { console.error('loadCalcs failed', e); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { loadCalcs(); }, [loadCalcs]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4" data-testid="rental-calc-section">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600 uppercase">Rental Calculations ({calcs.length})</p>
        <Button size="sm" className="bg-slate-900 text-xs" onClick={() => setShowNew(true)} data-testid="new-calc-btn">
          <Plus className="w-3 h-3 mr-1" /> New Calculation
        </Button>
      </div>

      {activeCalcId ? (
        <RentalCalcDetail calcId={activeCalcId} onRefresh={loadCalcs} />
      ) : calcs.length === 0 ? (
        <div className="text-center py-10 bg-white border border-slate-200 rounded-sm">
          <Calculator className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500 mb-1">No rental calculations yet</p>
          <p className="text-xs text-slate-400">Create a calculation to compute per-kWh pricing</p>
        </div>
      ) : null}

      {calcs.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">All Calculations</p>
          <div className="space-y-1">
            {calcs.map(c => (
              <button key={c.rental_calc_id} onClick={() => setActiveCalcId(c.rental_calc_id)}
                className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between transition-colors ${activeCalcId === c.rental_calc_id ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}>
                <span>{c.notes || 'Untitled'} — ₹{c.outputs?.service_fee_ex_gst?.toFixed(2) || '—'}/kWh</span>
                <CalcStatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </div>
      )}

      {showNew && <NewCalcDialog siteId={siteId} configSnapshot={configSnapshot} onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); setActiveCalcId(id); loadCalcs(); }} />}
    </div>
  );
}
